import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import invoiceLogo from "../../../../assets/images/invoicing-icon.svg";
import CommercialAirlineNavigationBar from "../../../../components/element/CommercialAirlineNavigationBar";
import ErrorPopup from "../../../../components/element/ErrorPopup";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { COMMERCIAL_INVOICE_STATUS, INVOICE_BREADCRUMB_SCREEN_NAME, PAGINATION_PAGE_SIZE } from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import "./InvoiceDetail.css";
import InvoiceTable from "./Tables/InvoiceTable";
import { ExportOutlined } from "@ant-design/icons";
import SendCommercialInvoiceDialog from "./views/SendCommercialInvoiceDialog";

function InvoiceDetail() {
  const location = useLocation();
  const { getInvoices, exportCommercialInvoiceDetails } = useAirlineAdminApi();
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const getStateData = () => {
    if (location.state) {
      sessionStorage.setItem(
        "invoiceDetailData",
        JSON.stringify(location.state)
      );
      return location.state;
    }
    const storedData = sessionStorage.getItem("invoiceDetailData");
    return storedData ? JSON.parse(storedData) : null;
  };

  const stateData = getStateData();
  const invoiceData = stateData?.invoiceData;
  const selectedAirlineId = stateData?.selectedAirlineId;
  const id = stateData?.id;
  const currentPage = stateData?.currentPage || 0;

  const {
    data: refreshedInvoiceData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "invoice-details",
      selectedAirlineId,
      invoiceData?.invoiceStatus,
      currentPage,
    ],
    queryFn: () =>
      getInvoices(
        selectedAirlineId,
        invoiceData?.invoiceStatus,
        currentPage,
        PAGINATION_PAGE_SIZE
      ),
    enabled:
      !!selectedAirlineId && !!invoiceData && !!invoiceData?.invoiceStatus,
  });

  const matchingInvoice = refreshedInvoiceData?.data?.content?.find(
    (invoice) => invoice.id === id
  );
  const filteredData = matchingInvoice ? [matchingInvoice] : [];

  const getBreadcrumbScreenName = (invoiceStatus) => {
    if (!invoiceStatus) return "";

    const statusArray = invoiceStatus.split(',');
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.PAID_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.PAID_INVOICE;
    }
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.SETTLED_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.SETTLED_INVOICE;
    }

    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.OVERDUE_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.OVERDUE_INVOICE;
    }

    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.PARTIAL_PAYMENT)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.NEW_INVOICE;
    }

    return INVOICE_BREADCRUMB_SCREEN_NAME[invoiceStatus] || "";
  };

  const invoiceBreadcrumbScreenName = getBreadcrumbScreenName(invoiceData?.invoiceStatus);

  const exportInvoiceMutation = useMutation({
    mutationFn: () =>
      exportCommercialInvoiceDetails(
        invoiceData?.dynamicsInvoiceNumber,
        invoiceData?.feeType
      ),
    onSuccess: () => {
      setShowExportDialog(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 4000);
    },
  });

  return (
    <div>
      <CommercialAirlineNavigationBar
        title={`Invoices for ${invoiceData?.dynamicsInvoiceNumber || ""}`}
        navigationLogo={invoiceLogo}
        fullWidthContent={true}
        backContent={
          <div className="navigation-header">
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
              className="navigation-header-link"
            >
              Home/
            </Link>
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE}
              className="navigation-header-link"
              onClick={() => sessionStorage.removeItem("invoiceDetailData")}
            >
              Invoices/{invoiceBreadcrumbScreenName}/
            </Link>
            {`${invoiceData?.dynamicsInvoiceNumber || ""}`}
          </div>
        }
      />

      {filteredData.length ? (
        <div className="invoice-export-button-container">
          <div
            className="invoice-export-button"
            onClick={() => setShowExportDialog(true)}
          >
            <ExportOutlined className="invoice-export-button-icon" />
            Export
          </div>
        </div>
      ) : null}

      {isLoading && <Loader />}
      {!isLoading && error && <ErrorPopup>{error.message}</ErrorPopup>}
      {!isLoading && !filteredData.length && <NoDataPlaceholder />}
      {filteredData.length && (
        <InvoiceTable
          invoicesLineItemData={filteredData[0]}
          invoiceStatus={invoiceData?.invoiceStatus}
        />
      )}

      <SendCommercialInvoiceDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onClickSend={() => exportInvoiceMutation.mutate()}
        isLoading={exportInvoiceMutation.isPending}
        showMessage={
          "Do you want to send the invoice flight details report to your registered email address?"
        }
        buttonText="Send"
        title="Send Invoice Flight Details Report"
      />

      {!exportInvoiceMutation.isPending && exportInvoiceMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {exportInvoiceMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}

      {showSuccessMessage && (
        <SnackbarAlert type="success">
          The invoice details are being generated and will be sent to your email once completed.
        </SnackbarAlert>
      )}
    </div>
  );
}

export default InvoiceDetail;